import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINTS } from "../../../api/header";
import apiClient from "../../../api/apiClient";
import { toast } from "sonner";
import { all_routes } from "../../../Router/all_routes";

// UI Components
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader, PageHeaderHeading } from "@/components/ui/page-header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReactSelect } from "@/components/ui/react-select";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";

// Icons
import { Plus, Trash2, Save, ArrowLeft } from "lucide-react";

const generateId = () => Math.random().toString(36).substr(2, 9);

const ConvertAssembleJobToSale = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentStore = useSelector((state) => state.current_store);
  const store_id = currentStore?.id || null;

  const [formData, setFormData] = useState({
    sale_inv_date: new Date().toISOString().split("T")[0],
    payment_status: "pending",
    notes: "",
    discount_type: "fixed",
    discount_value: "",
  });

  const [extraItems, setExtraItems] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(productSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [productSearch]);

  const { data: jobDetails, isLoading: isJobLoading } = useQuery({
    queryKey: ["assemble-job", id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.ASSEMBLE.JOB_DETAILS(id));
      return res.data?.data;
    },
    enabled: !!id,
  });

  const {
    data: productsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isProductsLoading,
  } = useInfiniteQuery({
    queryKey: ["assemble-products-extra", debouncedSearch, store_id],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get(
        API_ENDPOINTS.INVENTORY.POS_PRODUCTS,
        {
          params: {
            perPage: 15,
            page: pageParam,
            search: debouncedSearch,
            store_id,
          },
        },
      );
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta;
      if (meta && meta.current_page < meta.last_page) {
        return meta.current_page + 1;
      }
      return undefined;
    },
    staleTime: 60 * 1000 * 5,
  });

  const products = useMemo(() => {
    if (!productsData) return [];
    return productsData.pages.flatMap((page) => page.data || []);
  }, [productsData]);

  const productsLoading = isProductsLoading || isFetchingNextPage;

  const orderItemChange = (index, key, value) => {
    const newItems = [...extraItems];
    newItems[index][key] = value;
    if (key === "product" && value) {
      newItems[index].unit_price = value.price || 0;
      newItems[index].original_price = value.price || 0;
    }
    setExtraItems(newItems);
  };

  const addItem = () => {
    setExtraItems([
      ...extraItems,
      {
        id: generateId(),
        product_id: "",
        product: null,
        quantity: 1,
        unit_price: "",
        original_price: "",
        inventory_batch_id: "",
        inventory_serial_id: "",
      },
    ]);
  };

  const removeItem = (index) => {
    const newItems = [...extraItems];
    newItems.splice(index, 1);
    setExtraItems(newItems);
  };

  const convertMutation = useMutation({
    mutationFn: async (payload) => {
      return await apiClient.post(
        API_ENDPOINTS.ASSEMBLE.JOB_CONVERT_TO_SALE(id),
        payload,
      );
    },
    onSuccess: () => {
      toast.success("Job successfully converted to sale!");
      navigate(all_routes.assembleJobs);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to convert to sale");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.sale_inv_date) {
      toast.error("Sale invoice date is required");
      return;
    }

    let discountAmount = null;
    if (formData.discount_value) {
      const dv = parseFloat(formData.discount_value);
      if (formData.discount_type === "fixed") {
        discountAmount = dv;
      } else if (formData.discount_type === "percentage") {
        const extraTotal = extraItems.reduce(
          (acc, item) =>
            acc +
            parseFloat(item.unit_price || 0) * parseInt(item.quantity || 1, 10),
          0,
        );
        const subtotal = parseFloat(jobDetails?.total_price || 0) + extraTotal;
        discountAmount = (dv / 100) * subtotal;
      }
    }

    const payload = {
      sale_inv_date: formData.sale_inv_date,
      payment_status: formData.payment_status,
      notes: formData.notes || null,
      discount_type: formData.discount_type || null,
      discount_value: formData.discount_value
        ? parseFloat(formData.discount_value)
        : null,
      discount_amount:
        discountAmount !== null ? parseFloat(discountAmount.toFixed(2)) : null,
      extra_items:
        extraItems.length > 0
          ? extraItems.map((item) => ({
            product_id: item.product_id,
            quantity: parseInt(item.quantity, 10),
            unit_price: parseFloat(item.unit_price),
            original_price: parseFloat(item.original_price),
            inventory_batch_id: item.inventory_batch_id || null,
            inventory_serial_id: item.inventory_serial_id || null,
            discount_amount: item.discount_amount
              ? parseFloat(item.discount_amount)
              : null,
            discount_type: item.discount_type || null,
            discount_value: item.discount_value
              ? parseFloat(item.discount_value)
              : null,
            tax_amount: item.tax_amount ? parseFloat(item.tax_amount) : null,
            notes: item.notes || null,
          }))
          : null,
    };

    convertMutation.mutate(payload);
  };

  if (isJobLoading) {
    return <div className="p-8 text-center">Loading job details...</div>;
  }

  return (
    <PageContainer>
      <PageHeader>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <PageHeaderHeading
            title="Convert to Sale"
            subtitle={`Converting Assemble Job #${jobDetails?.job_number || id}`}
          />
        </div>
      </PageHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Sale Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">
                  Sale Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.sale_inv_date}
                  onChange={(e) =>
                    setFormData({ ...formData, sale_inv_date: e.target.value })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment">Payment Status</Label>
                <ReactSelect
                  value={{
                    value: formData.payment_status,
                    label:
                      formData.payment_status.charAt(0).toUpperCase() +
                      formData.payment_status.slice(1),
                  }}
                  onChange={(option) =>
                    setFormData({ ...formData, payment_status: option.value })
                  }
                  options={[
                    { value: "pending", label: "Pending" },
                    { value: "paid", label: "Paid" },
                    { value: "partial", label: "Partial" },
                    { value: "refunded", label: "Refunded" },
                  ]}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="discountType">Discount Type</Label>
                <ReactSelect
                  value={{
                    value: formData.discount_type,
                    label:
                      formData.discount_type === "fixed"
                        ? "Fixed Amount"
                        : "Percentage",
                  }}
                  onChange={(option) =>
                    setFormData({ ...formData, discount_type: option.value })
                  }
                  options={[
                    { value: "fixed", label: "Fixed Amount" },
                    { value: "percentage", label: "Percentage" },
                  ]}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="discountValue">Discount Value</Label>
                <Input
                  id="discountValue"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.discount_value}
                  onChange={(e) =>
                    setFormData({ ...formData, discount_value: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                placeholder="Additional sale notes..."
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Extra Items (Optional)</CardTitle>
            <Button
              type="button"
              onClick={addItem}
              size="sm"
              variant="secondary"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Extra Item
            </Button>
          </CardHeader>
          <CardContent>
            {extraItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border rounded-lg bg-muted/20">
                No extra items added.
              </div>
            ) : (
              <div className="space-y-3">
                {extraItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="flex flex-col md:flex-row items-start md:items-center gap-4 p-4 border rounded-lg shadow-sm"
                  >
                    <div className="w-full md:w-2/5">
                      <Label className="md:hidden mb-2 block">Product</Label>
                      <ReactSelect
                        isLoading={productsLoading}
                        onInputChange={(val) => setProductSearch(val)}
                        onMenuScrollToBottom={() => {
                          if (hasNextPage) fetchNextPage();
                        }}
                        filterOption={() => true}
                        value={(() => {
                          if (!item.product_id) return null;
                          const p =
                            item.product ||
                            products.find((p) => p.id === item.product_id);
                          return {
                            value: item.product_id,
                            label: p ? `${p.name}` : "Select Product",
                          };
                        })()}
                        onChange={(selectedOption) => {
                          const p = products.find(
                            (x) => x.id === selectedOption?.value,
                          );
                          orderItemChange(
                            index,
                            "product_id",
                            selectedOption?.value || "",
                          );
                          orderItemChange(index, "product", p || null);
                        }}
                        options={products.map((p) => ({
                          value: p.id,
                          label: p.name,
                        }))}
                        placeholder="Select Product..."
                      />
                    </div>

                    <div className="w-full md:w-20">
                      <Label className="md:hidden mb-2 block">Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) =>
                          orderItemChange(index, "quantity", e.target.value)
                        }
                      />
                    </div>

                    <div className="w-full md:w-32">
                      <Label className="md:hidden mb-2 block">Unit Price</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) =>
                          orderItemChange(index, "unit_price", e.target.value)
                        }
                      />
                    </div>

                    {item.product && item.product.is_batch_tracked === 1 && (
                      <div className="w-full md:w-40">
                        <Label className="md:hidden mb-2 block">Batch</Label>
                        <Input
                          placeholder="Batch ID"
                          value={item.inventory_batch_id}
                          onChange={(e) =>
                            orderItemChange(
                              index,
                              "inventory_batch_id",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    )}

                    {item.product && item.product.is_serial_tracked === 1 && (
                      <div className="w-full md:w-40">
                        <Label className="md:hidden mb-2 block">
                          Serial No
                        </Label>
                        <Input
                          placeholder="Serial ID"
                          value={item.inventory_serial_id}
                          onChange={(e) =>
                            orderItemChange(
                              index,
                              "inventory_serial_id",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    )}

                    <div className="w-full md:w-10 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => removeItem(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-end pt-6 border-t mt-4">
            <Button type="submit" disabled={convertMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {convertMutation.isPending ? "Converting..." : "Convert to Sale"}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </PageContainer>
  );
};

export default ConvertAssembleJobToSale;
